import React from 'react';
import {
  Autocomplete,
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { styled } from '@mui/system';

// Plain number formatter — stat driver values are unit counts (e.g. wRVUs), not dollar amounts
const numberFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const GroupHeader = styled('div')(({ theme }) => ({
  backgroundColor: 'white',
  cursor: 'pointer',
  fontWeight: 'bold',
  position: 'sticky',
  padding: '4px 10px',
  top: '-8px',
  '&:hover': {
    backgroundColor: '#b9dcff',
  }
}));

const GroupItems = styled('ul')({
  padding: 0,
});

/**
 * Component for selecting and configuring the stat driver
 */
export default function StatDriverInput({ 
  accounts, 
  statDriver, 
  months, 
  onAccountSelect, 
  onMonthlyValueChange 
}) {
  
  return (
    <Box>
      {/* Stat Driver Account Selection */}
      <Box sx={{ mb: 3 }}>
        <Autocomplete
          filterOptions={createFilterOptions({
            matchFrom: 'any',
            stringify: (option) => `${option.account_id} ${option.account_name} ${option.parent_account || ''}`,
          })}
          filterSelectedOptions
          groupBy={(account) => account.parent_account || 'Other'}
          options={[...(accounts || [])].sort((a, b) => {
            const parentA = a.parent_account || 'ZZZ'; // Put accounts without parent at the end
            const parentB = b.parent_account || 'ZZZ';
            return -parentB.localeCompare(parentA);
          })}
          getOptionLabel={(option) => {
            if (option) {
              return `${option.account_id} - ${option.account_name}`;
            }
            return '';
          }}
          value={statDriver.account || null}
          isOptionEqualToValue={(option, value) => option?.account_id === value?.account_id}
          onChange={(event, value) => {
            if (process.env.NODE_ENV !== 'production') {
              console.log('[StatDriverInput] Stat driver selected:', value);
            }
            if (onAccountSelect) {
              onAccountSelect(value);
            }
          }}
          renderGroup={(params) => (
            <li key={params.key}>
              <GroupHeader>
                {params.group}
              </GroupHeader>
              <GroupItems>{params.children}</GroupItems>
            </li>
          )}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Select Stat Driver Account" 
              helperText={`Choose the account to use as the statistical driver (e.g., wRVUs). ${accounts?.length || 0} accounts available.`}
            />
          )}
          renderOption={(props, account) => {
            const { key, ...restProps } = props;
            return (
              <li key={key} {...restProps}>
                {`${account.account_id} - ${account.account_name}`}
              </li>
            );
          }}
          noOptionsText={accounts?.length === 0 ? "No accounts loaded. Please complete Step 1 first." : "No matching accounts"}
        />
      </Box>

      {/* Show base period total if account selected */}
      {statDriver.account && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Base Period Total:</strong> {' '}
            {numberFormatter.format(statDriver.basePeriodTotal)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This is the total value from the Model After period. Variable accounts will scale based on the ratio of forecast values to this base total.
          </Typography>
        </Paper>
      )}

      {/* Monthly forecast inputs */}
      {statDriver.account && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Enter Forecast Values by Month
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Enter the forecasted stat driver values for each month in the forecast period.
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {months.map(month => (
              <Grid item xs={6} sm={4} md={3} key={month}>
                <TextField
                  fullWidth
                  type="number"
                  label={month}
                  value={statDriver.forecastMonthly[month] ?? ''}
                  onChange={(e) => {
                    if (onMonthlyValueChange) {
                      onMonthlyValueChange(month, e.target.value);
                    }
                  }}
                  inputProps={{
                    min: 0,
                    step: 'any',
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Show total if any values entered */}
          {Object.keys(statDriver.forecastMonthly).length > 0 && (
            <Paper elevation={1} sx={{ p: 2, mt: 3, backgroundColor: '#e3f2fd' }}>
              <Typography variant="subtitle2">
                <strong>Forecast Period Total:</strong> {' '}
                {numberFormatter.format(
                  Object.values(statDriver.forecastMonthly).reduce((sum, val) => sum + (Number(val) || 0), 0)
                )}
              </Typography>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}
